import { useEffect, useRef } from "react";

/**
 * Sticky header. Select-all covers the rows currently visible, which is what
 * a filtered grid implies — the caller passes the visible ids.
 *
 * A column with `sub` (a fixed tag group) takes a second header row: its name
 * spans its sub-columns, which are labelled underneath. Every other heading
 * spans both rows.
 *
 * `indeterminate` is a DOM property with no JSX attribute, so it is the one
 * thing set through a ref.
 *
 * @param {object} props
 * @param {{ key: string, label: string, width?: number, sub?: { key: string, label: string, width: number }[] }[]} props.columns visible columns
 * @param {boolean} props.allSelected
 * @param {boolean} props.someSelected
 * @param {boolean} [props.disabled]
 * @param {(selected: boolean) => void} props.onToggleAll
 */
export default function KpiGridHead({ columns, allSelected, someSelected, disabled, onToggleAll }) {
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const grouped = columns.some((column) => column.sub);
  const rowSpan = grouped ? 2 : undefined;

  return (
    <thead>
      <tr>
        <th className="kgb-col-select" rowSpan={rowSpan}>
          <input
            type="checkbox"
            className="kgb-checkbox"
            ref={checkboxRef}
            checked={allSelected}
            disabled={disabled}
            aria-label="Select all visible rows"
            onChange={(e) => onToggleAll(e.target.checked)}
          />
        </th>
        {columns.map((column) =>
          column.sub ? (
            <th key={column.key} colSpan={column.sub.length} className="kgb-th-group kgb-group-start">
              {column.label}
            </th>
          ) : (
            <th
              key={column.key}
              rowSpan={rowSpan}
              className={column.key === "title" ? "kgb-col-title" : undefined}
              style={{ minWidth: column.width }}
            >
              {column.label}
            </th>
          ),
        )}
      </tr>
      {grouped ? (
        <tr>
          {columns
            .filter((column) => column.sub)
            .map((column) =>
              column.sub.map((field, index) => (
                <th
                  key={`${column.key}-${field.key}`}
                  className={`kgb-th-sub${index === 0 ? " kgb-group-start" : ""}`}
                  style={{ minWidth: field.width }}
                >
                  {field.label}
                </th>
              )),
            )}
        </tr>
      ) : null}
    </thead>
  );
}
